// Batch H — Deal Detail "Criteria check" tab.
//
// Renders the per-rule pass/fail breakdown from
//   GET /api/deals/{id}/criteria-eval
//
// Layout:
//   1. Header strip — "Passes X of Y enabled rules" + top failures.
//   2. List of result rows: chip + label + 1-line summary.
//      Click a row to expand → full explanation + suggestion.
//   3. Empty state (no rules enabled) → CTA to Settings.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, CircleAlert, CircleCheck,
         CircleMinus, CircleX, ArrowRight, Settings as SettingsIcon } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/button";

function SeverityChip({ enabled, passed, severity }) {
  if (!enabled) {
    return (
      <span
        data-testid="criteria-chip-disabled"
        className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 ring-1 ring-slate-200"
      >
        <CircleMinus className="h-3 w-3" /> Off
      </span>
    );
  }
  if (passed === null) {
    return (
      <span
        data-testid="criteria-chip-skipped"
        className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ring-1 ring-slate-200"
      >
        <CircleAlert className="h-3 w-3" /> Skip
      </span>
    );
  }
  if (passed) {
    return (
      <span
        data-testid="criteria-chip-pass"
        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
      >
        <CircleCheck className="h-3 w-3" /> Pass
      </span>
    );
  }
  if (severity === "warn") {
    return (
      <span
        data-testid="criteria-chip-warn"
        className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
      >
        <CircleAlert className="h-3 w-3" /> Close
      </span>
    );
  }
  return (
    <span
      data-testid="criteria-chip-fail"
      className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 ring-1 ring-rose-200"
    >
      <CircleX className="h-3 w-3" /> Fail
    </span>
  );
}

function formatActual(r) {
  const u = r.unit || "";
  const v = r.actual_value;
  if (v === null || v === undefined) return "—";
  if (u === "$") {
    const sign = v < 0 ? "-" : "";
    return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (u === "%") return `${v.toFixed(2)}%`;
  if (u === "x") return `${v.toFixed(2)}x`;
  return String(v);
}

function formatTarget(r) {
  const u = r.unit || "";
  const v = r.target_value;
  if (v === null || v === undefined) return null;
  if (u === "$") {
    return `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (u === "%") return `${v.toFixed(2)}%`;
  if (u === "x") return `${v.toFixed(2)}x`;
  return String(v);
}

function CriteriaRow({ r }) {
  const [open, setOpen] = useState(false);
  const isClickable = r.enabled && (r.explanation || r.suggestion);

  const summary = r.enabled
    ? r.passed === null
      ? r.explanation || "Skipped"
      : r.passed
        ? `${r.label}: ${formatActual(r)} — passes the ${formatTarget(r)} target`
        : `${r.label}: ${formatActual(r)} vs target ${formatTarget(r)} — short by ${
            r.delta_percent != null ? Math.abs(r.delta_percent).toFixed(1) + "%" : "—"
          }`
    : "Not enabled in Settings → Purchase Criteria";

  return (
    <div
      data-testid={`criteria-result-${r.rule_id}`}
      className={`border-b border-slate-100 last:border-b-0 ${
        isClickable ? "cursor-pointer hover:bg-slate-50/60" : "opacity-70"
      }`}
      onClick={() => isClickable && setOpen((o) => !o)}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <SeverityChip enabled={r.enabled} passed={r.passed} severity={r.severity} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">{r.label}</p>
          <p className="text-xs text-slate-500">{summary}</p>
        </div>
        {isClickable ? (
          open ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )
        ) : null}
      </div>
      {open && isClickable ? (
        <div
          data-testid={`criteria-detail-${r.rule_id}`}
          className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 text-sm text-slate-700"
        >
          {r.explanation ? <p>{r.explanation}</p> : null}
          {r.suggestion ? (
            <p className="mt-2 inline-flex items-start gap-2 rounded-md border border-indigo-100 bg-white px-3 py-2 text-xs text-indigo-700">
              <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              {r.suggestion}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function CriteriaTab({ dealId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/deals/${dealId}/criteria-eval`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        console.warn("criteria-eval failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="criteria-tab-loading">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  if (!data) return null;

  const sm = data.summary || {};
  const noRulesEnabled = sm.enabled_count === 0;

  // Caption telling the user which price was used to evaluate the rules.
  // Phase 36 — three-state basis:
  //   • role=buyer + counter set → "Evaluated as Investor (Buyer side) at counter offer $X" (indigo)
  //   • role=buyer + no counter  → "Evaluated as Investor (Buyer side) at asking $X"        (slate)
  //   • role=seller (any state)  → "Evaluated as Owner (Seller side) at asking $X"          (amber)
  const basis = data.price_basis;
  const role = data.role || "buyer";
  const basisValue = data.price_basis_value;
  let basisCaption = null;
  let basisTone = "slate"; // tailwind classes derived below
  if (basisValue) {
    const fmtPrice = `$${Math.round(basisValue).toLocaleString()}`;
    if (basis === "asking_seller_view" || role === "seller") {
      basisCaption = `Evaluated as Owner (Seller side) at asking ${fmtPrice}`;
      basisTone = "amber";
    } else if (basis === "counter_offer") {
      basisCaption = `Evaluated as Investor (Buyer side) at counter offer ${fmtPrice}`;
      basisTone = "indigo";
    } else {
      basisCaption = `Evaluated as Investor (Buyer side) at asking ${fmtPrice}`;
      basisTone = "slate";
    }
  }
  const basisToneClass =
    basisTone === "amber"
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      : basisTone === "indigo"
        ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
        : "bg-slate-50 text-slate-600 ring-1 ring-slate-200";
  // Sort: failures first, then close, then pass, then skipped/off at bottom.
  const sortKey = (r) =>
    !r.enabled ? 4
    : r.passed === null ? 3
    : r.passed ? 2
    : r.severity === "warn" ? 1
    : 0;
  const sorted = [...data.results].sort((a, b) => sortKey(a) - sortKey(b));

  return (
    <div className="space-y-4" data-testid="criteria-tab">
      {/* Header strip */}
      <div
        data-testid="criteria-summary-strip"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        {noRulesEnabled ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-base font-medium text-slate-900">
              No criteria configured yet.
            </p>
            <p className="text-sm text-slate-600">
              Set up your investment thresholds in Settings → Purchase Criteria
              to see how this deal scores against your filters.
            </p>
            <Button
              data-testid="criteria-go-to-settings"
              onClick={() => navigate("/settings#criteria")}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <SettingsIcon className="mr-2 h-3.5 w-3.5" />
              Configure criteria
            </Button>
          </div>
        ) : (
          <>
            <p className="text-base text-slate-900">
              Passes{" "}
              <span
                data-testid="criteria-summary-fraction"
                className={`font-semibold tabular-nums ${
                  sm.failed_count === 0 ? "text-emerald-700" : "text-slate-900"
                }`}
              >
                {sm.passed_count} of {sm.evaluated_count}
              </span>{" "}
              evaluated rules
              {sm.enabled_count > sm.evaluated_count ? (
                <>
                  {" "}
                  ({sm.enabled_count - sm.evaluated_count}{" "}
                  {sm.enabled_count - sm.evaluated_count === 1 ? "rule" : "rules"} skipped — missing data)
                </>
              ) : null}
              .
            </p>
            {sm.top_failures?.length > 0 ? (
              <p className="mt-1 text-sm text-slate-600">
                Top failures:{" "}
                <span className="font-medium text-rose-700">
                  {sm.top_failures.join(", ")}
                </span>
              </p>
            ) : sm.failed_count === 0 ? (
              <p className="mt-1 text-sm text-emerald-700">
                Every enabled rule passes — this deal fits your filter.
              </p>
            ) : null}
            {basisCaption ? (
              <p
                data-testid="criteria-basis-caption"
                className={`mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium ${basisToneClass}`}
              >
                {basisCaption}
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* Results list */}
      {!noRulesEnabled ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {sorted
            .filter((r) => r.enabled)  // Hide rules the user hasn't enabled.
            .map((r) => (
              <CriteriaRow key={r.rule_id} r={r} />
            ))}
        </div>
      ) : null}
    </div>
  );
}
